import { Fragment } from "react";
import { useFormContext } from "react-hook-form";
import { StyleSheet } from "react-native";

import { useClientApi, useContentResource } from "@ctv/core";
import useUpload from "@ctv/core/api/useUpload";
import {
  DocumentField,
  InfoBox,
  Text,
  Token,
  View,
} from "@ctv/shared-core/src";
import { useDispatchSnackbar } from "@ctv/shared/contexts/SnackbarContext";

import IcSystemDownload from "@traveloka/icon-kit-web/react/IcSystemDownload";

import InformationDirectorAttorney from "./InformationDirectorAttorney";

import { useRegisterContent } from "../../../_contexts/RegisterContentContext";
import {
  DOWNLOAD_DOCUMENT_API,
  DownloadDocumentRequest,
  DownloadDocumentResponse,
  UPLOAD_DOCUMENT_API,
  UploadDocumentRequest,
  UploadDocumentResponse,
} from "../../../apis";
import { RegistrationFormValue } from "../../../types";
import { FieldRow } from "../../FieldRow";

export default function CreditCardDocumentForm() {
  const { token } = useRegisterContent();
  const { getValues } = useFormContext<RegistrationFormValue>();
  const cr = useContentResource().CorporateRegistration;

  const [paymentMethod, country] = getValues(["paymentMethod", "country"]);

  const openSnackbar = useDispatchSnackbar();
  const upload = useUpload<UploadDocumentResponse, UploadDocumentRequest>({
    path: UPLOAD_DOCUMENT_API,
  });
  const download = useClientApi<
    DownloadDocumentResponse,
    DownloadDocumentRequest
  >({
    domain: "management",
    method: "post",
    path: DOWNLOAD_DOCUMENT_API,
  });

  async function handleUpload(documentType: string, file: File) {
    const res = await upload(
      {
        country,
        documentType,
        paymentMethod: paymentMethod!,
        token,
      },
      file
    );

    if (res.success) return res.data;
    else throw res.error;
  }

  function handleDownloadAgreementForm() {
    download({
      documentType: "CC_AGREEMENT_FORM",
      token,
    })
      .then((res) => {
        if (res.success) {
          window.open(res.data.url, "_blank")?.focus();
        } else {
          openSnackbar({
            message: cr.failedDownloadErrorMessage,
            variant: "alert",
          });
        }
      })
      .catch(() =>
        openSnackbar({
          message: cr.failedDownloadErrorMessage,
          variant: "alert",
        })
      );
  }

  return (
    <Fragment>
      <View style={styles.divider} />
      <View style={[styles.content, styles.zIndex1]}>
        <InformationDirectorAttorney />
      </View>
      <View style={styles.divider} />
      <View style={styles.content}>
        <Text style={styles.marginBottomM} variant="title-1">
          {cr.agreementFormTitle}
        </Text>
        <InfoBox
          style={styles.marginBottomM}
          title={cr.agreementFormInfoBoxTitle}
          children={
            <Text ink="highlight" variant="ui-small">
              {cr.agreementFormInfoBoxContent}
            </Text>
          }
          variant="info"
          Action={
            <View row align="center" spacing="xxs">
              <IcSystemDownload width={18} height={18} />
              <Text
                ink="highlight"
                variant="ui-small"
                style={styles.infoTextAction}
                onPress={handleDownloadAgreementForm}
              >
                {cr.agreementFormInfoBoxActionText}
              </Text>
            </View>
          }
        />
        <FieldRow
          style={styles.fieldRow}
          label={cr.agreementFormSignedLabel}
          required
        >
          <DocumentField
            name="agreementDocument.AGREEMENT_FORM_CC"
            document={{
              docType: "AGREEMENT_FORM_CC",
              extensions: [".pdf", ".png", ".jpeg"],
              label: "",
              mandatory: true,
              maxSizeInMB: "20",
              placeholder: cr.requestNdaUploadPlaceholder,
              multiple: false,
              subLabel: null,
              tooltip: null,
              checkbox: null,
            }}
            onChange={handleUpload}
            documentExtensionCr={cr.documentExtension}
            documentFileSizeCr={cr.documentFileSize}
            documentRequiredCr={cr.documentRequired}
          />
        </FieldRow>
      </View>
    </Fragment>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: Token.spacing.l,
  },
  divider: {
    borderTopColor: Token.color.borderDivide,
    borderTopWidth: Token.borderWidth.thick,
  },
  fieldRow: {
    marginVertical: Token.spacing.xs,
  },
  infoTextAction: {
    cursor: "pointer",
    fontWeight: "500",
    marginTop: Token.spacing.xxs,
    marginLeft: Token.spacing.xxs,
  },
  // General styles
  marginBottomM: {
    marginBottom: Token.spacing.m,
  },
  zIndex1: {
    zIndex: 1,
  },
});
